import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { Reveal } from "../Reveal";
import { createBooking } from "../../lib/api";
import { ARTISTS as ROSTER } from "../../lib/artists";
import { X, Upload } from "lucide-react";

const ARTISTS = [
  "Jay",
  ...ROSTER.filter((a) => a.role === "Resident" && a.id !== "jay" && !a.adminOnly).map((a) => a.name),
  "No preference",
];

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const initialState = {
  name: "",
  email: "",
  phone: "",
  instagram: "",
  idea: "",
  placement: "",
  size: "",
  preferred_artist: "Jay",
};

export default function Booking() {
  const [form, setForm] = useState(initialState);
  const [files, setFiles] = useState([]); // {name, data_url, preview}
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onDrop = useCallback(async (accepted) => {
    const next = [];
    for (const f of accepted.slice(0, 8 - files.length)) {
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`${f.name} is larger than 5MB`);
        continue;
      }
      const data_url = await fileToDataUrl(f);
      next.push({ name: f.name, data_url, preview: data_url });
    }
    setFiles((prev) => [...prev, ...next].slice(0, 8));
  }, [files.length]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    maxFiles: 8,
  });

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        references: files.map(({ name, data_url }) => ({ name, data_url })),
      };
      await createBooking(payload);
      setSubmitted(true);
      setForm(initialState);
      setFiles([]);
      toast.success("Consultation request received");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="bg-white py-28 md:py-40 px-6 md:px-12"
    >
      <div className="max-w-[1100px] mx-auto">
        <div className="mb-14 md:mb-20 text-center">
          <Reveal delay={0.1}>
            <h2
              className="font-display text-[#0A0A0A] uppercase mx-auto whitespace-nowrap"
              style={{
                fontFamily: "'Jost', 'Futura', 'Helvetica Neue', sans-serif",
                fontSize: "clamp(1.4rem, 4vw, 3.4rem)",
                letterSpacing: "0.22em",
                paddingLeft: "0.22em",
                fontWeight: 200,
                lineHeight: 1.1,
              }}
            >
              REQUEST A CONSULTATION
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl mx-auto text-[#666] text-base md:text-lg font-light leading-relaxed">
              Share your idea, references and a placement. We respond within two working days,
              with the most suitable artist and a session estimate.
            </p>
          </Reveal>
        </div>

        {submitted ? (
          <Reveal>
            <div
              data-testid="booking-success"
              className="border-t border-b border-[#0A0A0A] py-20 text-center"
            >
              <div className="overline mb-6">Received</div>
              <h3 className="font-display text-3xl md:text-5xl tracking-tight mb-6">
                <span className="font-serif-italic">Thank you.</span>
              </h3>
              <p className="text-[#666] max-w-lg mx-auto">
                Your request has been logged. We&rsquo;ll write to you from{" "}
                <span className="text-[#0A0A0A]">jayinkacademy@gmail.com</span> within 48 hours.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                data-testid="booking-new-request"
                className="mt-10 inline-block text-xs tracking-[0.25em] uppercase border-b border-[#0A0A0A] pb-1"
              >
                Submit another request
              </button>
            </div>
          </Reveal>
        ) : (
          <Reveal delay={0.1}>
            <form
              onSubmit={submit}
              data-testid="booking-form"
              className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2"
            >
              <Field id="name" label="Name" required value={form.name} onChange={update("name")} />
              <Field id="email" label="Email" type="email" required value={form.email} onChange={update("email")} />
              <Field id="phone" label="Phone" type="tel" required value={form.phone} onChange={update("phone")} />
              <Field id="instagram" label="Instagram handle" placeholder="@yourhandle" value={form.instagram} onChange={update("instagram")} />

              <div className="md:col-span-2">
                <label htmlFor="idea" className="overline block mt-8 mb-3">Tattoo idea</label>
                <textarea
                  id="idea"
                  data-testid="booking-idea"
                  required
                  rows={4}
                  value={form.idea}
                  onChange={update("idea")}
                  className="editorial-input resize-none"
                  placeholder="Describe the concept, mood, references…"
                />
              </div>

              <Field id="placement" label="Placement" required placeholder="e.g. inner forearm" value={form.placement} onChange={update("placement")} />
              <Field id="size" label="Approx. size" required placeholder="e.g. 10 × 12 cm" value={form.size} onChange={update("size")} />

              <div className="md:col-span-2">
                <label htmlFor="preferred_artist" className="overline block mt-8 mb-3">
                  Preferred artist
                </label>
                <select
                  id="preferred_artist"
                  data-testid="booking-artist"
                  value={form.preferred_artist}
                  onChange={update("preferred_artist")}
                  className="editorial-input cursor-pointer"
                >
                  {ARTISTS.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2 mt-8">
                <div className="overline mb-3">Reference images <span className="normal-case tracking-normal text-[#999]">(up to 8 · max 5MB each)</span></div>
                <div
                  {...getRootProps()}
                  data-testid="booking-dropzone"
                  className={`border border-dashed py-10 px-6 text-center cursor-pointer transition-colors ${
                    isDragActive ? "border-[#0A0A0A] bg-[#FAFAFA]" : "border-[#CCCCCC] hover:border-[#0A0A0A]"
                  }`}
                >
                  <input {...getInputProps()} data-testid="booking-file-input" />
                  <Upload size={20} className="mx-auto mb-3 text-[#666]" />
                  <p className="text-sm text-[#666]">
                    {isDragActive ? "Drop the files…" : "Drag references here, or click to select"}
                  </p>
                </div>
                {files.length > 0 && (
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-4">
                    {files.map((f, i) => (
                      <div key={i} className="relative aspect-square bg-[#F5F5F5] overflow-hidden">
                        <img src={f.preview} alt={f.name} className="w-full h-full object-cover" style={{ filter: "grayscale(100%)" }} />
                        <button
                          type="button"
                          data-testid={`booking-remove-file-${i}`}
                          onClick={() => setFiles((p) => p.filter((_, idx) => idx !== i))}
                          className="absolute top-1 right-1 bg-white/90 p-1 hover:bg-white"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="md:col-span-2 mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <p className="text-xs text-[#999] tracking-wide">
                  By submitting, you agree to be contacted by JAY — INK ACADEMY regarding your request.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="booking-submit-button"
                  className="inline-flex items-center gap-3 bg-[#0A0A0A] text-white px-10 py-4 text-xs tracking-[0.28em] uppercase font-medium hover:bg-[#333] transition-colors disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Submit request"} <span>→</span>
                </button>
              </div>
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
}

function Field({ id, label, type = "text", required, placeholder, value, onChange }) {
  return (
    <div>
      <label htmlFor={id} className="overline block mt-8 mb-3">
        {label}{required && <span className="text-[#0A0A0A]"> *</span>}
      </label>
      <input
        id={id}
        data-testid={`booking-${id}`}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="editorial-input"
      />
    </div>
  );
}
